import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  BriefcaseBusiness,
  CheckCircle,
  Clock,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";

const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const subtlePlaceholderClass = "placeholder:text-muted-foreground/40";

const initialFormState = {
  fullName: "",
  phone: "",
  email: "",
  company: "",
  serviceRequired: "",
  projectScale: "",
  serviceLocation: "",
  requirements: "",
};

const contactInfo = [
  {
    icon: MapPin,
    title: "Visit Us",
    content: "#106, House of Lords, St Mark's Road, Bengaluru - 560001",
    href: "https://maps.google.com/?q=106,House of lords, St mark's road, Bengaluru - 560001",
  },
  {
    icon: Phone,
    title: "Call Us",
    content: "9986435437",
    href: "tel:9986435437",
  },
  {
    icon: Mail,
    title: "Email Us",
    content: "care@bryninteriors.com",
    href: "mailto:care@bryninteriors.com",
  },
  {
    icon: Clock,
    title: "Working Hours",
    content: "Mon - Sat | 9:00 AM - 6:00 PM",
    href: "",
  },
];

const serviceAreas = [
  "Workplace Interiors",
  "Retail Fit-Outs",
  "Hospitality Spaces",
  "Residential Interiors",
  "Civil & MEP Coordination",
  "Turnkey Execution",
];

const faqs = [
  {
    question: "What project types does BRYN handle?",
    answer:
      "We work across commercial interiors, retail spaces, hospitality projects, residential interiors, and turnkey fit-out execution.",
  },
  {
    question: "Do you take care of end-to-end execution?",
    answer:
      "Yes. BRYN supports projects from concept coordination and procurement through site execution and final handover.",
  },
  {
    question: "Can you work with our architect or consultant team?",
    answer:
      "Absolutely. We often collaborate with external architects, PMC teams, and client-side stakeholders to ensure smooth execution.",
  },
  {
    question: "Do you handle only Bengaluru projects?",
    answer:
      "Bengaluru is our base, but we can discuss nearby and multi-location project requirements depending on scope.",
  },
  {
    question: "How quickly can you respond to an enquiry?",
    answer:
      "We aim to review serious project enquiries promptly and usually respond after understanding the required scope and location.",
  },
  {
    question: "Can you support renovation and phased upgrades?",
    answer:
      "Yes. We also support refurbishments, workspace upgrades, and phased interior improvements where continuity matters.",
  },
];

const ContactPage = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [captchaToken, setCaptchaToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const captchaRef = useRef(null);
  const resetTimerRef = useRef(null);
  const formSectionRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (location.hash !== "#contact-form" || !formSectionRef.current) {
      return;
    }

    const scrollToForm = () => {
      const top =
        formSectionRef.current.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top, behavior: "smooth" });
    };

    const timer = window.setTimeout(scrollToForm, 120);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const resetForm = () => {
    setFormData(initialFormState);
    setCaptchaToken("");
    captchaRef.current?.reset();
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken("");
    captchaRef.current?.reset();
    toast.error("reCAPTCHA expired. Please verify again.");
  };

  const handleCaptchaErrored = () => {
    setCaptchaToken("");
    captchaRef.current?.reset();
    toast.error("reCAPTCHA could not be verified. Please try again.");
  };

  const updateField = (field, value) => {
    const normalizedValue =
      field === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setFormData((prev) => ({ ...prev, [field]: normalizedValue }));
  };

  const validateForm = () => {
    if (!formData.fullName || !formData.phone || !formData.serviceRequired) {
      toast.error("Please complete the required fields.");
      return false;
    }

    if (!PHONE_REGEX.test(formData.phone)) {
      toast.error("Phone number must be a valid 10-digit Indian mobile number.");
      return false;
    }

    if (formData.email && !EMAIL_REGEX.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }

    if (!RECAPTCHA_SITE_KEY) {
      toast.error("reCAPTCHA site key is missing. Add REACT_APP_RECAPTCHA_SITE_KEY to enable submissions.");
      return false;
    }

    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA verification.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          service_required: formData.serviceRequired,
          project_scale: formData.projectScale,
          service_location: formData.serviceLocation,
          additional_requirements: formData.requirements,
          recaptcha_token: captchaToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Unable to submit contact request");
      }

      setIsSubmitted(true);
      toast.success("Thanks for reaching out. We'll contact you shortly.");
      resetForm();

      resetTimerRef.current = window.setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      toast.error("Could not send your enquiry right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden bg-background">
      <section className="relative min-h-[55vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1639663742190-1b3dba2eebcf?w=1920&auto=format&fit=crop"
            alt="Contact BRYN"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/75 to-charcoal/55" />
        </div>

        <div className="relative z-10 container mx-auto px-4 pb-16 pt-28 lg:px-8 lg:pb-24">
          <div className="max-w-3xl">
            <Badge className="mb-5 border-0 bg-primary/20 text-primary-foreground">
              CONTACT US
            </Badge>
            <h1 className="font-display text-4xl font-bold leading-tight text-primary-foreground sm:text-5xl lg:text-6xl">
              GET IN TOUCH
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              Planning a fit-out, renovation, or turnkey interior project? Let&apos;s
              discuss the scope, location, and timeline so we can guide you with
              the right next steps.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {contactInfo.map((item) => (
              <Card key={item.title} className="border-0 bg-card shadow-md">
                <CardContent className="p-6">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground">
                    {item.title}
                  </h3>
                  {item.href ? (
                    <a
                      href={item.href}
                      target={item.icon === MapPin ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className="mt-3 block text-sm leading-relaxed text-muted-foreground transition-colors hover:text-primary"
                    >
                      {item.content}
                    </a>
                  ) : (
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {item.content}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section ref={formSectionRef} id="contact-form" className="pb-16 sm:pb-20 lg:pb-24 scroll-mt-28">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
            <Card className="border-0 bg-card shadow-elegant">
              <CardContent className="p-6 sm:p-8 lg:p-10">
                {isSubmitted ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                      <CheckCircle className="h-8 w-8 text-success" />
                    </div>
                    <h3 className="mt-5 font-display text-2xl font-semibold text-foreground">
                      Enquiry sent successfully
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      Thanks for reaching out. We&apos;ll review your requirement and connect soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
                        Project Enquiry
                      </Badge>
                      <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
                        Tell us about your project
                      </h2>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="contact-full-name">Full Name *</Label>
                        <Input
                          id="contact-full-name"
                          value={formData.fullName}
                          onChange={(event) => updateField("fullName", event.target.value)}
                          placeholder="Full Name"
                          className={`h-12 ${subtlePlaceholderClass}`}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-phone">Phone *</Label>
                        <div className="flex h-12 overflow-hidden rounded-md border border-input bg-background">
                          <div className="flex items-center gap-2 border-r border-input bg-muted/60 px-3 text-sm font-medium text-foreground">
                            <span className="text-base" aria-hidden="true">🇮🇳</span>
                            +91
                          </div>
                          <Input
                            id="contact-phone"
                            value={formData.phone}
                            onChange={(event) => updateField("phone", event.target.value)}
                            placeholder="Phone Number"
                            inputMode="numeric"
                            maxLength={10}
                            className={`h-full flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 ${subtlePlaceholderClass}`}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="contact-email">Email</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={formData.email}
                          onChange={(event) => updateField("email", event.target.value)}
                          placeholder="Email Address"
                          className={`h-12 ${subtlePlaceholderClass}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-company">Company / Organization</Label>
                        <Input
                          id="contact-company"
                          value={formData.company}
                          onChange={(event) => updateField("company", event.target.value)}
                          placeholder="Company / Organization"
                          className={`h-12 ${subtlePlaceholderClass}`}
                        />
                      </div>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Service Required *</Label>
                        <Select
                          value={formData.serviceRequired}
                          onValueChange={(value) => updateField("serviceRequired", value)}
                        >
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="commercial-interiors">Commercial Interiors</SelectItem>
                            <SelectItem value="retail-fit-out">Retail Fit-Out</SelectItem>
                            <SelectItem value="hospitality-interiors">Hospitality Interiors</SelectItem>
                            <SelectItem value="residential-interiors">Residential Interiors</SelectItem>
                            <SelectItem value="turnkey-execution">Turnkey Execution</SelectItem>
                            <SelectItem value="design-consultation">Design Consultation</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Project Size / Scope</Label>
                        <Select
                          value={formData.projectScale}
                          onValueChange={(value) => updateField("projectScale", value)}
                        >
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select project size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single-space">Single Space</SelectItem>
                            <SelectItem value="small-office">Small Office / Unit</SelectItem>
                            <SelectItem value="multi-floor">Multi-Floor Fit-Out</SelectItem>
                            <SelectItem value="renovation">Renovation / Upgrade</SelectItem>
                            <SelectItem value="turnkey-large">Large Turnkey Project</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-location">Service Location</Label>
                      <Input
                        id="contact-location"
                        value={formData.serviceLocation}
                        onChange={(event) => updateField("serviceLocation", event.target.value)}
                        placeholder="Project Location"
                        className={`h-12 ${subtlePlaceholderClass}`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-requirements">Additional Requirements</Label>
                      <Textarea
                        id="contact-requirements"
                        value={formData.requirements}
                        onChange={(event) => updateField("requirements", event.target.value)}
                        className={`min-h-[140px] ${subtlePlaceholderClass}`}
                        placeholder="Tell us about timeline, scope, site condition, or execution expectations."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>reCAPTCHA *</Label>
                      {RECAPTCHA_SITE_KEY ? (
                        <div className="overflow-x-auto rounded-xl border border-border/60 p-3">
                          <ReCAPTCHA
                            ref={captchaRef}
                            sitekey={RECAPTCHA_SITE_KEY}
                            onChange={(value) => setCaptchaToken(value || "")}
                            onExpired={handleCaptchaExpired}
                            onErrored={handleCaptchaErrored}
                          />
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 text-sm text-muted-foreground">
                          Add `REACT_APP_RECAPTCHA_SITE_KEY` to enable reCAPTCHA on this form.
                        </div>
                      )}
                    </div>

                    <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
                      {isSubmitting ? "Sending..." : "Submit Enquiry"}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="overflow-hidden border-0 shadow-md">
                <div className="aspect-[4/3]">
                  <iframe
                    src="https://www.google.com/maps?q=12.971602,77.594563&output=embed"
                    title="BRYN Location"
                    className="h-full w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </Card>

              <Card className="border-0 bg-charcoal text-primary-foreground shadow-md">
                <CardContent className="p-6">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15">
                    <BriefcaseBusiness className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold">Quick Contact</h3>
                  <p className="mt-3 text-sm leading-relaxed text-primary-foreground/75">
                    For immediate discussions around fit-outs, procurement, or execution support, call or email us directly.
                  </p>
                  <div className="mt-6 space-y-3 text-sm">
                    <a href="tel:9986435437" className="block text-primary-foreground/85">
                      9986435437
                    </a>
                    <a href="mailto:care@bryninteriors.com" className="block text-primary-foreground/85">
                      care@bryninteriors.com
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 bg-muted shadow-md">
                <CardContent className="p-6">
                  <h3 className="font-display text-2xl font-semibold text-foreground">
                    Service Areas
                  </h3>
                  <div className="mt-5 flex flex-wrap gap-3">
                    {serviceAreas.map((area) => (
                      <Badge key={area} variant="outline" className="border-primary/20 bg-background text-foreground">
                        {area}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-muted py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
              FAQs
            </Badge>
            <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
              Answers to common project questions
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {faqs.map((faq) => (
              <Card key={faq.question} className="border-0 bg-card shadow-md">
                <CardContent className="p-6">
                  <h3 className="font-display text-xl font-semibold text-foreground">
                    {faq.question}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
